import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Loader from '../shared/Loader';
import { AuthContext } from '../../providers/AuthContext';
import Swal from 'sweetalert2';

const ChatStats = () => {
  const { token } = useContext(AuthContext);
  const [chatHealth, setChatHealth] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [healthRes, settingsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL || '/api'}/chat/health`),
        axios.get(`${process.env.REACT_APP_API_URL || '/api'}/settings`)
      ]);
      setChatHealth(healthRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setChatHealth({ status: 'error', error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChat = async () => {
    if (!settings) return;
    setToggling(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL || '/api'}/settings`, {
        isChatEnabled: !settings.isChatEnabled
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSettings(response.data);
      
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: response.data.isChatEnabled ? 'Chat IA Activado' : 'Chat IA Desactivado',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    } catch (error) {
      console.error('Error toggling chat:', error);
      Swal.fire('Error', 'No se pudo cambiar el estado del chat', 'error');
    } finally {
      setToggling(false);
    }
  };

  const testChat = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await axios.post('/api/chat', {
        messages: [{ role: 'user', content: 'Hola, esto es una prueba' }]
      });
      
      setTestResult({
        success: true,
        reply: response.data.reply,
        timestamp: new Date().toLocaleString()
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message,
        timestamp: new Date().toLocaleString()
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="chatstats-panel">
      {/* Header with Toggle */}
      <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-4 rounded-4 shadow-sm border">
        <div>
          <h4 className="fw-bold mb-1">Interruptor Global del Chat</h4>
          <p className="text-muted mb-0">Habilita o deshabilita el widget de inteligencia artificial en toda la tienda.</p>
        </div>
        <div className="d-flex align-items-center">
          <span className={`fw-bold me-3 ${settings?.isChatEnabled ? 'text-success' : 'text-danger'}`}>
            {settings?.isChatEnabled ? 'ENCENDIDO' : 'APAGADO'}
          </span>
          <div className="form-check form-switch" style={{ transform: 'scale(1.5)', margin: 0 }}>
            <input 
              className="form-check-input" 
              type="checkbox" 
              role="switch" 
              id="chatToggleSwitch"
              checked={settings?.isChatEnabled || false}
              onChange={handleToggleChat}
              disabled={toggling}
              style={{ cursor: toggling ? 'wait' : 'pointer' }}
            />
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="chatstats-status-row">
        <div className={`chatstats-status-card ${chatHealth?.status === 'ok' ? 'ok' : 'error'}`}>
          <div className="chatstats-status-icon">
            <i className={`fas ${chatHealth?.status === 'ok' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
          </div>
          <div className="chatstats-status-info">
            <span className="chatstats-status-label">Estado del Servicio</span>
            <span className="chatstats-status-value">
              {chatHealth?.status === 'ok' ? 'Funcionando' : 'Error'}
            </span>
          </div>
        </div>

        <div className={`chatstats-status-card ${chatHealth?.openai === 'configured' ? 'ok' : 'warn'}`}>
          <div className="chatstats-status-icon">
            <i className={`fas ${chatHealth?.openai === 'configured' ? 'fa-brain' : 'fa-exclamation-triangle'}`}></i>
          </div>
          <div className="chatstats-status-info">
            <span className="chatstats-status-label">OpenAI API</span>
            <span className="chatstats-status-value">
              {chatHealth?.openai === 'configured' ? 'Conectado' : 'Desconectado'}
            </span>
          </div>
        </div>

        <div className="chatstats-status-card neutral">
          <div className="chatstats-status-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="chatstats-status-info">
            <span className="chatstats-status-label">Última Verificación</span>
            <span className="chatstats-status-value" style={{ fontSize: '0.85rem' }}>
              {chatHealth?.timestamp ? new Date(chatHealth.timestamp).toLocaleString() : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="chatstats-actions-card shadow-sm border-0 rounded-4">
        <h5 className="fw-bold mb-3">Diagnóstico del Asistente</h5>
        <div className="chatstats-actions-row">
          <button className="chatstats-test-btn" onClick={testChat} disabled={testing}>
            {testing ? (
              <><i className="fas fa-spinner fa-spin"></i> Ejecutando prueba...</>
            ) : (
              <><i className="fas fa-play"></i> Simular Chat</>
            )}
          </button>
          <button className="chatstats-refresh-btn" onClick={fetchData}>
            <i className="fas fa-sync-alt"></i> Actualizar Estado
          </button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`chatstats-result mt-4 ${testResult.success ? 'success' : 'error'}`}>
            <div className="chatstats-result-header">
              <i className={`fas ${testResult.success ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
              <strong>Resultado de la prueba</strong>
              <small>{testResult.timestamp}</small>
            </div>
            {testResult.success ? (
              <p className="chatstats-result-text">
                ✅ Conexión exitosa y respuesta generada<br />
                <small className="text-muted">"{testResult.reply?.substring(0, 150)}..."</small>
              </p>
            ) : (
              <p className="chatstats-result-text">❌ Error: {testResult.error}</p>
            )}
          </div>
        )}
      </div>

      {/* Features */}
      <div className="chatstats-features-card">
        <h4>Funcionalidades disponibles</h4>
        <div className="chatstats-features-grid">
          <div className="chatstats-feature">
            <i className="fas fa-search" style={{ color: '#3b82f6' }}></i>
            <span>Búsqueda de productos</span>
          </div>
          <div className="chatstats-feature">
            <i className="fas fa-box" style={{ color: '#06b6d4' }}></i>
            <span>Consulta de órdenes</span>
          </div>
          <div className="chatstats-feature">
            <i className="fas fa-tags" style={{ color: '#10b981' }}></i>
            <span>Exploración de categorías</span>
          </div>
          <div className="chatstats-feature">
            <i className="fas fa-chart-bar" style={{ color: '#f59e0b' }}></i>
            <span>Verificación de stock</span>
          </div>
          <div className="chatstats-feature">
            <i className="fas fa-robot" style={{ color: '#8b5cf6' }}></i>
            <span>IA conversacional</span>
          </div>
          <div className="chatstats-feature">
            <i className="fas fa-mobile-alt" style={{ color: '#1a1a1a' }}></i>
            <span>Diseño responsivo</span>
          </div>
        </div>
      </div>

      {/* Config Note */}
      {chatHealth?.openai !== 'configured' && (
        <div className="chatstats-note">
          <i className="fas fa-info-circle"></i>
          <div>
            <strong>Nota:</strong> El chat funciona con reglas básicas. Para respuestas más inteligentes,
            configura <code>OPENAI_API_KEY</code> en las variables de entorno del backend.
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatStats;
